import SeoService from '@/shared/services/SeoService';

/**
 * @typedef {Object} NoticeRecord
 * @property {string|number} [id]
 * @property {string} [title]
 * @property {string} [slug]
 * @property {string} [description]
 * @property {Record<string, unknown>} [key]
 */

/**
 * @typedef {Object} NoticeSearchOneResponse
 * @property {NoticeRecord} [notice]
 * @property {NoticeRecord} [data]
 * @property {object} [seo]
 */

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export function isNoticeEntityId(value) {
  return UUID_RE.test(String(value ?? '').trim());
}

/** Normalizuje odgovor POST /notice/search-by-document. */
export function parseNoticeSearchByDocumentResponse(response) {
  if (!response || typeof response !== 'object') return null;
  const notice = response.notice ?? response.data?.notice ?? null;
  const seo = response.seo ?? response.data?.seo ?? null;
  if (!notice?.id) return null;
  const slug = getNoticeSlugFromRecord({ ...notice, seo });
  const title = (notice.title ?? '').trim() || null;
  return {
    notice,
    seo,
    slug,
    title,
    noticeId: notice.id,
  };
}

/**
 * Normalizuje odgovor POST /notice/search-one (wrapper ili ravno telo).
 * @param {NoticeSearchOneResponse|NoticeRecord|null|undefined} response
 * @returns {NoticeRecord|null}
 */
export function parseNoticeSearchOneResponse(response) {
  if (!response || typeof response !== 'object') return null;
  return response.notice ?? response.data?.notice ?? response.data ?? response;
}

/** Normalizuje odgovor POST /notice/save-or-update. */
export function parseNoticeSaveResponse(response) {
  const parsed = parseNoticeSearchOneResponse(response);
  if (parsed && typeof parsed === 'object' && parsed.id != null) return parsed;
  if (response && typeof response === 'object' && response.id != null) return response;
  return parsed ?? response ?? null;
}

/** ID sačuvane objave iz save-or-update odgovora. */
export function getNoticeIdFromSaveResponse(response) {
  const notice = parseNoticeSaveResponse(response);
  return notice?.id ?? response?.id ?? null;
}

/**
 * Extract public permalink slug from a notice record returned by list/search APIs.
 */
export function getNoticeSlugFromRecord(notice) {
  if (!notice || typeof notice !== 'object') return null;

  const candidates = [
    notice.slug,
    notice.seo_slug,
    notice.seoSlug,
    notice.permalink,
    notice.url_slug,
    notice.urlSlug,
    notice.seo?.slug,
    notice.seo?.permalink,
    notice.meta?.slug,
    notice.meta_slug,
  ];

  for (const value of candidates) {
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
  }

  return null;
}

/**
 * Normalizuje odgovor POST /seo/search-one.
 * @param {unknown} response
 * @returns {object|null}
 */
export function parseSeoSearchOneResponse(response) {
  if (!response || typeof response !== 'object') return null;
  return response.data ?? response;
}

/**
 * Izvlači slug iz SEO zapisa (search-one po entity_id).
 * @param {unknown} seo
 * @returns {string|null}
 */
export function getSlugFromSeoRecord(seo) {
  const record = parseSeoSearchOneResponse(seo) ?? seo;
  if (!record || typeof record !== 'object') return null;

  const direct = record.slug ?? record.permalink ?? record.url_slug ?? record.urlSlug;
  if (typeof direct === 'string' && direct.trim()) return direct.trim();

  return getNoticeSlugFromRecord(record);
}

/**
 * Učitava javni slug objave preko POST /seo/search-one (entity_id).
 * @param {string|number} entityId
 * @param {AbortSignal|null} [signal]
 * @returns {Promise<string|null>}
 */
export async function fetchNoticeSlugByEntityId(entityId, signal = null) {
  const id = String(entityId ?? '').trim();
  if (!id) return null;

  try {
    const seoRaw = await SeoService.searchOne(id, signal);
    return getSlugFromSeoRecord(seoRaw);
  } catch {
    return null;
  }
}

/**
 * Javni slug iz notice zapisa ili SEO search-one (entity_id).
 * @param {Object} notice
 * @param {AbortSignal|null} [signal]
 * @returns {Promise<string|null>}
 */
export async function resolveNoticePublicSlug(notice, signal = null) {
  const direct = getNoticeSlugFromRecord(notice);
  if (direct) return direct;

  const id = notice?.id;
  if (id == null || id === '') return null;

  return fetchNoticeSlugByEntityId(id, signal);
}

/**
 * Dopunjava listu objava slug-om iz SEO kada /notice/search ne vraća slug.
 */
export async function enrichNoticesWithPublicSlugs(notices, signal = null) {
  if (!Array.isArray(notices) || notices.length === 0) return [];

  return Promise.all(
    notices.map(async (notice) => {
      const slug = await resolveNoticePublicSlug(notice, signal);
      return slug ? { ...notice, slug } : notice;
    })
  );
}

/** Parsira slug iz PostgreSQL / API poruke (npr. Key (slug)=(t) already exists.). */
export function parseSlugFromConflictMessage(message) {
  const text = String(message ?? '');
  const keyMatch = text.match(/\(slug\)=\(([^)]+)\)/i);
  if (keyMatch?.[1]) return keyMatch[1].trim();
  const quotedMatch = text.match(/slug[=:\s]+["']?([a-z0-9-]+)["']?/i);
  return quotedMatch?.[1]?.trim() ?? null;
}

/** Da li API greška ukazuje da objava nema slug ili je poslat pogrešan search-one zahtev. */
export function isNoticeMissingSlugError(err) {
  const detailsSlug = String(err?.response?.data?.details?.slug ?? '').toLowerCase();
  if (
    detailsSlug.includes('blank') ||
    detailsSlug.includes('must not') ||
    detailsSlug.includes('empty')
  ) {
    return true;
  }

  const message = String(err?.response?.data?.message || err?.message || '').toLowerCase();
  return (
    message.includes('slug') &&
    (message.includes('must have') ||
      message.includes('must contain') ||
      message.includes('must not') ||
      message.includes('missing') ||
      message.includes('required') ||
      message.includes('blank') ||
      message.includes('obavezan') ||
      message.includes('обавезан'))
  );
}

/**
 * Korisnička poruka na srpskom za grešku pri učitavanju objave u admin formi.
 * @param {unknown} err
 * @returns {string}
 */
export function getNoticeLoadErrorMessage(err) {
  if (isNoticeMissingSlugError(err)) {
    return 'Објава нема подешен URL slug. Подаци се не могу учитати — контактирајте администратора или покушајте поново.';
  }

  const backendMessage = String(err?.response?.data?.message || err?.message || '').trim();
  if (backendMessage) {
    return `Грешка при учитавању објаве: ${backendMessage}`;
  }

  return 'Грешка при учитавању објаве. Молимо покушајте поново.';
}

/** Normalizuje API grešku u Error sa korisničkom porukom. */
export function toNoticeLoadError(err) {
  const message = getNoticeLoadErrorMessage(err);
  const next = new Error(message);
  next.cause = err;
  if (err?.response) next.response = err.response;
  return next;
}

/** Da li API greška ukazuje na zauzet slug. */
export function isSlugConflictError(err) {
  const status = err?.response?.status;
  const code = String(err?.response?.data?.code || err?.code || '');
  const message = String(err?.response?.data?.message || err?.message || '').toLowerCase();
  return (
    status === 409 ||
    code === 'ConflictException' ||
    code === 'SLUG_ALREADY_EXISTS' ||
    (message.includes('slug') &&
      (message.includes('already exists') ||
        message.includes('already exist') ||
        message.includes('unique') ||
        message.includes('duplicate')))
  );
}

/**
 * Korisnička poruka za konflikt slug-a.
 * @param {unknown} err
 * @param {string} [attemptedSlug]
 */
export function getSlugConflictUserMessage(err, attemptedSlug) {
  const backendMessage = String(err?.response?.data?.message || err?.message || '');
  const takenSlug = parseSlugFromConflictMessage(backendMessage) || attemptedSlug || '';
  const slugPart = takenSlug ? `„${takenSlug}"` : '';
  return {
    fieldError: slugPart
      ? `URL slug ${slugPart} већ постоји. Изаберите други slug за јавну адресу објаве.`
      : 'Овај URL slug већ постоји. Изаберите други slug за јавну адресу објаве.',
    toast: slugPart
      ? `Објава је сачувана. URL slug ${slugPart} већ постоји — измените slug у SEO подешавањима и сачувајте поново.`
      : 'Објава је сачувана. Овај URL slug већ постоји — измените slug у SEO подешавањима и сачувајте поново.',
    takenSlug,
  };
}